import { RefObject, useEffect, useRef, useCallback } from "react";

import { UseFormReturn } from "react-hook-form";

import { useLiveFormEditorContext } from "../common/LiveFormEditorContext";
import type { StepTicketsFormData } from "../Steps/StepTickets/types";

import { useScrollPreviewWithArrow } from "./useScrollPreviewWithArrow";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { TICKETING_SELECTION_V2_CATEGORIES_ID } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/SortByCategoryPills/SortByCategoryPills";
import { useArrow } from "@/components/LiveEditorElements/AnimatePreview/Arrow/ArrowContext";

export const useAnimatePreviewOnRateEvents = (
  formContainerRef: RefObject<HTMLElement | null>,
  form?: UseFormReturn<StepTicketsFormData>
) => {
  const { currentStepIndex: currentStep, previewDevice } = useLiveFormEditorContext();
  const { hideArrow } = useArrow();
  const { scrollAndUpdateArrow } = useScrollPreviewWithArrow();
  const focusedRateIdRef = useRef<string | undefined>(undefined);
  const previousRateIdsRef = useRef<string[]>([]);

  const handleRateInFocusChange = useCallback(
    (rateIdInFocus: string | undefined) => {
      if (focusedRateIdRef.current !== rateIdInFocus) {
        hideArrow();
        focusedRateIdRef.current = rateIdInFocus;
      }
      scrollAndUpdateArrow({ rateIdInFocus });
    },
    [hideArrow, scrollAndUpdateArrow]
  );

  const getRateIdAtIndex = useCallback(
    (index: number): string | undefined => form?.getValues(`rates.${index}.id`) ?? undefined,
    [form]
  );

  // 1. Animate preview when the step mount/changes and viewport mode changes
  useEffect(() => {
    // Delay the anim to make sure the preview is mounted
    const timeoutId = setTimeout(() => {
      scrollAndUpdateArrow({ rateIdInFocus: focusedRateIdRef.current ?? getRateIdAtIndex(0) });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [currentStep, previewDevice, scrollAndUpdateArrow, getRateIdAtIndex]);

  // 2. Animate preview when form values change
  useEffect(() => {
    const subscription = form?.watch((values, { name }) => {
      let rateIdInFocus: string | undefined;

      if (name?.endsWith(".categoryIds")) {
        focusedRateIdRef.current = undefined;
        scrollAndUpdateArrow({ targetElementId: TICKETING_SELECTION_V2_CATEGORIES_ID });
        return;
      }

      // Case 1: Individual rate field changes (e.g., name, price, etc.)
      if (name?.startsWith("rates.")) {
        const match = name.match(/^rates\.(\d+)/);
        if (match) {
          rateIdInFocus = getRateIdAtIndex(parseInt(match[1], 10));
        }

        const currentRateIds = form.getValues("rates")?.map((rate) => rate?.id || "") || [];
        previousRateIdsRef.current = currentRateIds;
      }
      // Case 2: Entire rates array changes (e.g., add, delete, reorder)
      else if (name === "rates") {
        const rates = values.rates;
        if (Array.isArray(rates)) {
          const currentRateIds = rates.map((rate) => rate?.id || "");
          const previousRateIds = previousRateIdsRef.current;

          // Case 2a: Rate was added
          if (currentRateIds.length > previousRateIds.length) {
            rateIdInFocus = currentRateIds.find((id) => !previousRateIds.includes(id));
          }
          // Case 2b: Rate was deleted
          else if (currentRateIds.length < previousRateIds.length) {
            rateIdInFocus = currentRateIds[0];
          }

          // Update references after handling the change
          previousRateIdsRef.current = currentRateIds;
        }
      }

      // Skip animation if no rate is in focus (it is necessary when the added rates as already been handled by Case 1 when the new rate is registered)
      if (rateIdInFocus === undefined) {
        return;
      }

      // Animate to the focused rate
      handleRateInFocusChange(rateIdInFocus);
    });

    return () => subscription?.unsubscribe();
  }, [form, scrollAndUpdateArrow, currentStep, handleRateInFocusChange, getRateIdAtIndex]);

  // 3. Animate preview when focusing in the form
  useEffect(() => {
    const formElement = formContainerRef.current;
    if (!formElement) {
      return;
    }

    const handleFocusIn = (event: FocusEvent) => {
      const card = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-index]");
      const index = card?.getAttribute("data-index");
      const rateIdInFocus = index ? getRateIdAtIndex(parseInt(index, 10)) : undefined;

      if (!rateIdInFocus) {
        return;
      }

      handleRateInFocusChange(rateIdInFocus);
    };

    formElement.addEventListener("focusin", handleFocusIn);
    return () => {
      formElement.removeEventListener("focusin", handleFocusIn);
    };
  }, [formContainerRef, scrollAndUpdateArrow, hideArrow, handleRateInFocusChange, getRateIdAtIndex]);

  // 4. Animate preview when the preview device changes
  useEffect(() => {
    scrollAndUpdateArrow({ rateIdInFocus: focusedRateIdRef.current ?? getRateIdAtIndex(0) });
  }, [previewDevice, scrollAndUpdateArrow, getRateIdAtIndex]);
};
